import { matchPath } from 'react-router-dom';

import { getProject } from 'util/getProject';
import { isArrayEmpty } from 'util/isArrayEmpty';
import { mapLifecycleTypeToLifecycleLabel } from 'util/mapLifecycleTypeToLifecycleLabel';
import { selectSurveyUiUrl } from 'util/selector/apiSelector';
import { createResourceContentSelector } from 'util/selector/resourceSelector';
import { selectHasMatch, selectLocationPath, selectMatch } from 'util/selector/routerSelector';

import { AdminRoute } from 'app/route/admin';
import { ClientRoute } from 'app/route/client';
import { ExternalRoute } from 'app/route/external';
import memoize from 'lodash/memoize';
import { ClientPlanType } from 'model/ClientPlanType';
import { LifecycleType } from 'model/Lifecycle';
import { NavigationNode } from 'model/NavigationNode';
import { PageItemKind } from 'model/PageItemKind';
import { ProjectModel } from 'api/dto/Project.dto';
import { ProjectKind } from 'model/ProjectKind';
import { ProjectStatus } from 'model/ProjectStatus';
import { PulseTemplateModel } from 'api/dto/PulseTemplate.dto';
import { Question } from 'model/Question';
import { QuestionOpinion } from 'model/QuestionOpinion';
import { LoadingStateEnum, Resource } from 'model/Resource';
import { formValueSelector } from 'redux-form';
import { AdminState } from 'reduxStore/appStore';
import { selectQuestions } from 'reduxStore/page/selectors';
import { ProjectState } from 'reduxStore/project/initialState';
import {
  selectIsProjectTemplate,
  selectProjectTemplateList,
} from 'reduxStore/projectTemplate/selectors';
import { selectWebLinkDistributionResource } from 'reduxStore/webLink/selectors';
import { AdminFormNames } from 'register/AdminFormNames';
import { createSelector, Selector } from 'reselect';
import { SortDirection } from 'shared/constants';
import { Company } from 'api/dto/Company.dto';
import { PulseProjectApi } from 'api/PulseProjectApi';

export function selectProjectDomain(state: AdminState): ProjectState.Domain {
  return state.project as ProjectState.Domain;
}

export const selectProjectListResource = createSelector(
  selectProjectDomain,
  (domain) =>
    (domain && domain.listResource) || Resource.createPendingResource<ProjectState.ProjectList>(),
);

export const selectProjectListPagination = createSelector(
  selectProjectDomain,
  (domain) => domain && domain.listPagination,
);

export const selectCurrentProjectResource = createSelector(
  selectProjectDomain,
  (domain) =>
    domain?.currentResource || Resource.createPendingResource<ProjectModel | PulseTemplateModel>(),
);

export const selectCurrentProjectContent = createResourceContentSelector(
  selectCurrentProjectResource,
);

export const selectCurrentPulseTemplate = createSelector(
  selectCurrentProjectContent,
  (pulseTemplate) => {
    if (!PulseTemplateModel.isPulseTemplateModel(pulseTemplate)) {
      console.error('Trying to get pulse template data on normal project page!');
      return null;
    }
    return pulseTemplate;
  },
);

export const selectCurrentProject = createSelector(
  selectCurrentProjectContent,
  (projectOrTemplate) => {
    return getProject(projectOrTemplate);
  },
);

export const selectIsCurrentProjectClosed = createSelector(
  selectCurrentProject,
  (project) => project.status === ProjectStatus.Closed,
);

export const selectProjectFrequency = createSelector(
  selectCurrentProject,
  (project) => project.pulseFrequencyId,
);

export const selectChildProjectsResource = createSelector(selectCurrentPulseTemplate, (project) => {
  if (!project || !PulseTemplateModel.isPulseTemplateModel(project)) {
    return Resource.createPendingResource<ProjectModel[]>();
  }

  return Resource.createResolvedResource<ProjectModel[]>({
    data: PulseTemplateModel.getChildProjects(project),
  });
});

export const selectCurrentCompany = createSelector(
  selectCurrentProject,
  (project) => project?.company,
);

export const selectCompanyCircleId = createSelector(
  selectCurrentCompany,
  (company) => company?.circleId,
);

export const selectCurrentCompanyPlan = createSelector(
  selectCurrentCompany,
  (company) => company?.plan,
);

export const selectIsMultiLanguageEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.multiLanguageEnabled,
);

export const selectIsCustomKioskCodesEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.customKioskCodesEnabled,
);

export const selectAiSummariesEnabled = createSelector(selectCurrentCompany, (company) => {
  return Company.getAiSummariesEnabled(company);
});

export const selectHeadlinesEnabled = createSelector(selectCurrentCompany, (company) => {
  return Company.getHeadlinesEnabled(company);
});

export const selectActEnabled = createSelector(selectCurrentCompany, (company) => {
  return company ? Company.getActEnabled(company) : false;
});

export const selectIsRespondentsApiEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.hasRespondentExternalEventApiEnabled,
);

export const selectHasDemographyExternalEventApiEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.hasDemographyExternalEventApiEnabled,
);

export const selectHasDepartmentExternalEventApiEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.hasDepartmentExternalEventApiEnabled,
);

export const selectHasAvailableSurveysLinkEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.hasAvailableSurveysLinkEnabled,
);

export const selectHasLifecycleSubscriptionExternalEventApiEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.hasLifecycleSubscriptionExternalEventApiEnabled,
);

export const selectHasHrisApiEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.hasHrisApiEnabled,
);

export const selectEnabledLocales = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.enabledLocales,
);

export const selectEnabledDashboardLocales = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.enabledDashboardLocales,
);

export const selectIsDashboardLanguagesEnabled = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.isDashboardLanguagesEnabled,
);

export const selectShouldTranslateComments = createSelector(
  selectCurrentCompany,
  (company) => company?.settings.shouldTranslateComments,
);

export const selectIsEssentialPlan = createSelector(
  selectCurrentCompanyPlan,
  (plan) => plan && plan === ClientPlanType.Essential,
);

export const selectIsProfessionalPlan = createSelector(
  selectCurrentCompanyPlan,
  (plan) => plan && plan === ClientPlanType.Professional,
);

export const selectIsEnterprisePlan = createSelector(
  selectCurrentCompanyPlan,
  (plan) => plan && plan === ClientPlanType.Enterprise,
);

export const selectIsLegacyPlan = createSelector(
  selectCurrentCompanyPlan,
  (plan) => plan && plan === ClientPlanType.Legacy,
);

export const selectHasProfessionalOrEnterprisePermission = createSelector(
  selectIsProfessionalPlan,
  selectIsEnterprisePlan,
  selectIsLegacyPlan,
  (isProfessionalPlan, isEnterprisePlan, isLegacy) =>
    isEnterprisePlan || isProfessionalPlan || isLegacy,
);

export const selectCurrentLiveStop = createSelector(
  selectCurrentProject,
  (project) => project && project.liveStop,
);

export const selectCurrentCompanyId = createSelector(selectCurrentCompany, (company) =>
  company ? company.id : null,
);

export const selectCurrentCompanyName = createSelector(selectCurrentCompany, (company) =>
  company ? company.name : null,
);

export const selectCurrentKind = createSelector(selectCurrentProject, (project) =>
  project ? project.kind : null,
);

export const createProjectTypeFormSelector = formValueSelector(AdminFormNames.CreateProjectType);

export const selectProjectType: Selector<AdminState, boolean> = (state) =>
  createProjectTypeFormSelector(state, 'type');

export const selectCurrentProjectId = createSelector(
  selectMatch(
    AdminRoute.PROJECT_PATTERN,
    AdminRoute.PROJECT_TEMPLATE_PATTERN,
    ClientRoute.PROJECT_PATTERN,
  ),
  (match) => match && parseInt(match.params.id, 10),
);

export const selectCurrentProjectOnlyId = createSelector(
  selectMatch(AdminRoute.PROJECT_PATTERN, ClientRoute.PROJECT_PATTERN),
  (match) => match && parseInt(match.params.id, 10),
);

export const selectIsProjectTypeSelected = createSelector(selectProjectType, (projectType) =>
  Boolean(projectType),
);

export const selectIsProjectPublished = createSelector(
  selectCurrentProject,
  (project) => project && project.isPublished,
);

export const selectProjectPreviewUrlResource = createSelector(
  selectSurveyUiUrl,
  selectWebLinkDistributionResource,
  (surveyUiUrl, webLinkDistributionResource): Resource<string | null> => {
    if (!webLinkDistributionResource) {
      return Resource.createPendingResource<string | null>(null);
    }

    if (
      webLinkDistributionResource.loadingState === LoadingStateEnum.RESOLVED &&
      webLinkDistributionResource.data
    ) {
      return Resource.createResolvedResource({
        data: `${surveyUiUrl}/${webLinkDistributionResource.data.id}/preview`,
      });
    }

    if (webLinkDistributionResource.loadingState === LoadingStateEnum.REJECTED) {
      return Resource.createRejectedResource({
        error: webLinkDistributionResource.error,
      });
    }

    return Resource.createPendingResource<string | null>(null);
  },
);

export const selectIsProjectKindCAS = createSelector(
  selectCurrentKind,
  (kind) => kind === ProjectKind.PROJECT_CLIENT_ADVOCACY_SCORE,
);

export const selectIsSurveyTypeEngagementScore = createSelector(
  selectCurrentKind,
  (kind) => kind === ProjectKind.PROJECT_ENGAGEMENT_SCORE,
);

export const selectIsSurveyTypeOverallScore = createSelector(
  selectCurrentKind,
  (kind) => kind === ProjectKind.PROJECT_OVERALL_SCORE,
);

// it will search in question list for checkbox/radio question
export const selectHasInsightQuestion = createSelector(
  selectQuestions,
  (questions: Question[]) =>
    questions &&
    !!questions.find((question: Question) =>
      [PageItemKind.QuestionRadioButton, PageItemKind.QuestionCheckbox].includes(question.kind),
    ),
);

export const selectHasProjectCASQuestion = createSelector(
  selectIsProjectKindCAS,
  selectQuestions,
  (isCASSurvey: boolean, questions: Question[]) =>
    isCASSurvey &&
    questions &&
    !!questions.find((question: Question) => question.kind === PageItemKind.QuestionCAS),
);

export const selectHasProjectOpinionQuestion = createSelector(
  selectIsSurveyTypeEngagementScore,
  selectQuestions,
  (isEngagementScore: boolean, questions: Question[]) =>
    isEngagementScore &&
    questions &&
    !!questions
      .filter((question) => question.kind === PageItemKind.QuestionOpinion)
      .find(
        (opinionQuestion: QuestionOpinion) =>
          opinionQuestion.category?.name === 'Employee Engagement',
      ),
);

export const selectIsCurrentProjectFixableOnly = createSelector(
  selectCurrentProject,
  (project) => project && ProjectModel.isFixableOnly(project),
);

export const selectFilteredTemplateList = createSelector(
  selectProjectTemplateList,
  selectCurrentKind,
  (templateList, currentProjectKind) =>
    templateList && templateList.filter((template) => template.kind === currentProjectKind),
);

export const selectIsPulseTemplate = createSelector(
  selectCurrentKind,
  (kind) => kind && kind === ProjectKind.PULSE_TEMPLATE,
);

export const selectPulseProjects = createSelector(
  selectCurrentPulseTemplate,
  (project) => project && project.pulseProjects,
);

export const selectPulseLifecycleProjects = createSelector(
  selectCurrentPulseTemplate,
  (project) =>
    project && PulseTemplateModel.isPulseTemplateModel(project) && project.pulseLifecycleProjects,
);

export const selectDemandPolls = createSelector(
  selectCurrentPulseTemplate,
  (project) =>
    project && PulseTemplateModel.isPulseTemplateModel(project) && project.pulseOnDemandPolls,
);

export const selectFixablePulseProjects = createSelector(
  selectPulseProjects,
  (projects: ProjectModel[]) =>
    projects && projects.filter((project) => ProjectModel.isFixableOnly(project)),
);

export const selectHasPulseProjects = createSelector(
  selectPulseProjects,
  (pulseProjects) => pulseProjects && !isArrayEmpty(pulseProjects),
);

export const selectSubProjectCurrentId = createSelector(
  selectMatch(ClientRoute.Project.Survey.DEVELOP_PATTERN),
  (match) => match && parseInt(match.params.survey_id, 10),
);

export const selectCurrentPulseProject = createSelector(
  selectPulseProjects,
  selectSubProjectCurrentId,
  (projects, projectId) => projects && projects.find((item: ProjectModel) => item.id === projectId),
);

export const selectDemandAndPulseAndLifecycle = createSelector(
  selectPulseProjects,
  selectDemandPolls,
  selectPulseLifecycleProjects,
  (pulses, demands, lifecycles) => [
    ...(pulses ? pulses : []),
    ...(demands ? demands : []),
    ...(lifecycles ? lifecycles : []),
  ],
);

const getLiveChildProjects = (projectOrTemplate: ProjectModel | PulseTemplateModel) =>
  PulseTemplateModel.isPulseTemplateModel(projectOrTemplate)
    ? [
        ...projectOrTemplate.pulseProjects,
        ...projectOrTemplate.pulseOnDemandPolls,
        ...projectOrTemplate.pulseLifecycleProjects,
      ].filter((project) => ProjectModel.isLive(project))
    : [];

/** Pulse surveys, on-demand polls and lifecycle surveys the programme lists as Live. */
export const selectLiveChildProjects = createSelector(
  selectCurrentProjectContent,
  getLiveChildProjects,
);

/**
 * Unlike `selectChildProjectsResource`, follows the project request itself - a failed load is
 * REJECTED rather than a permanent pending - and includes lifecycle surveys. A resolved project
 * that is not a pulse template stays PENDING: it has no child surveys to report, and reporting an
 * empty list would let a screen conclude the programme has nothing live.
 */
export const selectLiveChildProjectsResource = createSelector(
  selectCurrentProjectResource,
  selectCurrentProjectContent,
  (projectResource, projectOrTemplate): Resource<ProjectModel[]> => {
    if (projectResource.loadingState === LoadingStateEnum.REJECTED) {
      return Resource.createRejectedResource<ProjectModel[]>({ error: projectResource.error });
    }

    if (
      projectResource.loadingState === LoadingStateEnum.PENDING ||
      !PulseTemplateModel.isPulseTemplateModel(projectOrTemplate)
    ) {
      return Resource.createPendingResource<ProjectModel[]>();
    }

    return Resource.createResolvedResource<ProjectModel[]>({
      data: getLiveChildProjects(projectOrTemplate),
    });
  },
);

export const selectCurrentSubProject = createSelector(
  selectDemandAndPulseAndLifecycle,
  selectSubProjectCurrentId,
  (projects, currentProjectId): ProjectModel =>
    projects && projects.find((item: ProjectModel) => item.id === currentProjectId),
);

export const selectLifecycleType = createSelector(
  selectCurrentSubProject,
  (project) => project && project.lifecycleType,
);

export const selectCurrentSubProjectId = createSelector(
  selectCurrentSubProject,
  (project) => project?.id,
);

export const selectIsFirstOnboardSurvey = createSelector(
  selectPulseLifecycleProjects,
  selectCurrentSubProjectId,
  (lifecycleProjects, currentSubProjectId) => {
    if (!lifecycleProjects || !currentSubProjectId) {
      return true;
    }
    const [firstOnboard] = [...lifecycleProjects]
      .filter((project) => project.lifecycleType === LifecycleType.ONBOARD)
      .sort((a, b) => a.id - b.id);
    return !firstOnboard || firstOnboard.id === currentSubProjectId;
  },
);

export const selectCurrentSubProjectKind = createSelector(
  selectCurrentSubProject,
  (project) => project && project.kind,
);

export const selectDemandPollsToCopy = createSelector(
  selectCurrentPulseTemplate,
  selectSubProjectCurrentId,
  (project, demandPollId) => {
    return project && PulseTemplateModel.isPulseTemplateModel(project)
      ? project.pulseOnDemandPolls
          .filter((poll) => poll.id !== demandPollId)
          .sort(ProjectModel.sortByLiveStop)
      : ([] as ProjectModel[]);
  },
);

export const selectIsDemandPoll = createSelector(
  selectCurrentSubProject,
  (project) => project && ProjectModel.isDemandPoll(project),
);

export const selectIsLifecycleProject = createSelector(
  selectCurrentSubProject,
  (project) => project && ProjectModel.isLifecycleProject(project),
);

export const selectCurrentPulseProjectResponseRate = createSelector(
  selectCurrentSubProject,
  (project) => project && project.responseRate,
);

export const selectNextPulseProject = createSelector(
  selectPulseProjects,
  selectSubProjectCurrentId,
  (pulseProjects, projectId) => {
    let currentIndex;

    if (!isArrayEmpty(pulseProjects) && projectId) {
      currentIndex = pulseProjects.findIndex((project: ProjectModel) => project.id === projectId);
    }

    return pulseProjects[currentIndex + 1] ? pulseProjects[currentIndex + 1] : undefined;
  },
);

export const selectSubProjectLiveStart = createSelector(
  selectCurrentProject,
  selectCurrentSubProject,
  (project, subProject) => {
    if (subProject) {
      return subProject.liveStart;
    }

    return project && project.liveStart;
  },
);

export const selectSubProjectLiveStop = createSelector(
  selectCurrentProject,
  selectCurrentSubProject,
  (project, subProject) => {
    if (subProject) {
      return subProject.liveStop;
    }

    return project && project.liveStop;
  },
);

export const selectPrevPulseProject = createSelector(
  selectPulseProjects,
  selectSubProjectCurrentId,
  (pulseProjects, projectId) => {
    let currentIndex;

    if (!isArrayEmpty(pulseProjects) && projectId) {
      currentIndex = pulseProjects.findIndex((project: ProjectModel) => project.id === projectId);
    }

    return currentIndex !== 0 ? pulseProjects[currentIndex - 1] : undefined;
  },
);

export const selectHasPulseProject = createSelector(
  selectPulseProjects,
  (projects) => projects && !isArrayEmpty(projects),
);

export const selectIsProjectEditable = createSelector(
  selectCurrentProject,
  selectIsProjectTemplate,
  selectHasMatch(ClientRoute.Project.Survey.DEVELOP_PATTERN),
  selectCurrentSubProject,
  selectIsLifecycleProject,
  (project, isTemplate, isPulseTemplate, currentSubProject, isLifecycleProject) => {
    return (
      isLifecycleProject ||
      isTemplate ||
      (isPulseTemplate
        ? currentSubProject && ProjectModel.isEditable(currentSubProject)
        : project && ProjectModel.isEditable(project))
    );
  },
);

export const selectIsCategoryEditable = createSelector(
  selectCurrentProject,
  selectIsProjectTemplate,
  selectHasMatch(ClientRoute.Project.Survey.DEVELOP_PATTERN),
  selectCurrentSubProject,
  selectIsLifecycleProject,
  (project, isTemplate, isPulseTemplate, currentSubProject, isLifecycleProject) => {
    if (isLifecycleProject || isTemplate) {
      return true;
    }
    const target = isPulseTemplate ? currentSubProject : project;
    return target && (ProjectModel.isEditable(target) || ProjectModel.isLive(target));
  },
);

export const selectIsCurrentSubProjectFixableOnly = createSelector(
  selectCurrentSubProject,
  (project) => project && ProjectModel.isFixableOnly(project),
);

export const selectIsCurrentSubProjectClosed = createSelector(
  selectCurrentSubProject,
  (project) => project && ProjectModel.isClosed(project),
);

export const selectClosedPulseProjects = createSelector(
  selectPulseProjects,
  selectHasPulseProject,
  (projects, hasPulseProject) =>
    projects && hasPulseProject
      ? projects.filter((item: ProjectModel) => item.status === ProjectStatus.Closed)
      : [],
);

export const selectIsAnyPulseProjectClosed = createSelector(
  selectClosedPulseProjects,
  (projectsList) => !isArrayEmpty(projectsList),
);

export const selectLastClosePulseProject = createSelector(
  selectClosedPulseProjects,
  (projects) => projects && projects.at(-1),
);

export const selectInDevelopmentPulseProjects = createSelector(
  selectPulseProjects,
  selectHasPulseProject,
  (projects, hasPulseProject) =>
    projects && hasPulseProject
      ? projects.filter((item: ProjectModel) => item.status === ProjectStatus.InDevelopment)
      : [],
);

export const selectLivePulseProjects = createSelector(
  selectPulseProjects,
  selectHasPulseProject,
  (projects, hasPulseProject) =>
    projects && hasPulseProject
      ? projects.filter((item: ProjectModel) => item.status === ProjectStatus.Live)
      : [],
);

export const selectLiveDemandProjects = createSelector(selectDemandPolls, (projects) =>
  projects ? projects.filter((item: ProjectModel) => item.status === ProjectStatus.Live) : [],
);

export const selectHasLivePulseOrDemandProjects = createSelector(
  selectLivePulseProjects,
  selectLiveDemandProjects,
  (pulses, demands) => pulses.length > 0 || demands.length > 0,
);

export const selectIsVisibleQuestionLibraryAddButton = createSelector(
  selectInDevelopmentPulseProjects,
  (pulsesInDevelopment) => pulsesInDevelopment.length > 0,
);

export const selectInDevPulseProjectsWithoutCurrent = memoize(
  (id: number): Selector<AdminState, ProjectModel[]> =>
    createSelector(
      selectInDevelopmentPulseProjects,
      (list) => list && list.filter((item: ProjectModel) => item.id !== id),
    ),
);

export const selectLibraryQuestions = createSelector(
  selectCurrentProject,
  (project: ProjectModel) => project && ProjectModel.getQuestionLibrary(project),
);

export const selectLibraryQuestionsGroupedByCategory = createSelector(
  selectLibraryQuestions,
  (questions) => {
    if (!questions) return [];

    const grouped: Record<number, Question[]> = {};

    for (const question of questions) {
      const categoryId = (question as QuestionOpinion).category?.id;
      if (categoryId && !grouped[categoryId]) {
        grouped[categoryId] = [];
      }
      if (categoryId) {
        grouped[categoryId].push(question);
      }
    }

    return Object.values(grouped).flat();
  },
);

export const selectFirstPulseProjectInDevelopment = createSelector(
  selectInDevelopmentPulseProjects,
  (project) => project && project[0],
);

export const selectIsPulseFromPast = createSelector(
  selectCurrentPulseProject,
  (project) => project && project.createdInPast,
);

const locationQueries: { [key: string]: PulseProjectApi.FetchSubProjectsQuery } = {
  [ClientRoute.Project.Survey.LIVE_AND_SCHEDULED]: {
    kind: ProjectKind.PULSE_PROJECT,
    status: [ProjectStatus.Live, ProjectStatus.InDevelopment],
    order: SortDirection.ASC,
    page: 1,
  },
  [ClientRoute.Project.Survey.ARCHIVED]: {
    kind: ProjectKind.PULSE_PROJECT,
    status: [ProjectStatus.Closed],
    order: SortDirection.DESC,
    page: 1,
  },
  [ClientRoute.Project.DemandPoll.LIVE_AND_SCHEDULED]: {
    kind: ProjectKind.PULSE_ON_DEMAND_PROJECT,
    status: [ProjectStatus.Live, ProjectStatus.InDevelopment],
    order: SortDirection.ASC,
    page: 1,
  },
  [ClientRoute.Project.DemandPoll.ARCHIVED]: {
    kind: ProjectKind.PULSE_ON_DEMAND_PROJECT,
    status: [ProjectStatus.Closed],
    order: SortDirection.DESC,
    page: 1,
  },
};

export const selectSubProjectsPageQuery = createSelector(selectLocationPath, (currentLocation) => {
  const query = Object.keys(locationQueries).find((location) => {
    return !!matchPath(currentLocation, { path: location, exact: false });
  });

  return query ? locationQueries[query] : ({} as PulseProjectApi.FetchSubProjectsQuery);
});

export const selectReportUILinks = createSelector(
  selectCurrentPulseTemplate,
  (project): NavigationNode[] => {
    if (
      !project ||
      !PulseTemplateModel.isPulseTemplateModel(project) ||
      !project.project.isPublished
    ) {
      return [];
    }

    const reportUILinks: NavigationNode[] = [];

    let id = ProjectModel.lastPublishedProjectId(project.pulseProjects);
    if (typeof id === 'number') {
      reportUILinks.push({
        href: ExternalRoute.ProjectReportSummary.replace(/:id/, id.toString()),
        label: 'Engage (Pulse Surveys)',
      });
    }

    id = ProjectModel.lastPublishedProjectId(project.pulseOnDemandPolls);
    if (typeof id === 'number') {
      reportUILinks.push({
        href: ExternalRoute.ProjectReportSummary.replace(/:id/, id.toString()),
        label: 'On Demand Polls',
      });
    }
    const lifecycleProjects: ProjectModel[] = project.pulseLifecycleProjects;

    lifecycleProjects.forEach((lifecycleProject) => {
      if (lifecycleProject && lifecycleProject.isPublished) {
        reportUILinks.push({
          href: ExternalRoute.ProjectReportSummary.replace(/:id/, lifecycleProject.id.toString()),
          label: mapLifecycleTypeToLifecycleLabel(lifecycleProject.lifecycleType),
        });
      }
    });
    return reportUILinks;
  },
);

export const selectCurrentProjectName = createSelector(
  selectCurrentProject,
  (project) => project?.name || null,
);

export const selectCurrentProjectLiveStart = createSelector(
  selectCurrentProject,
  (project) => project?.liveStart || null,
);

export const selectCurrentProjectLiveStop = createSelector(
  selectCurrentProject,
  (project) => project?.liveStop || null,
);

export const selectHasProjectNameAndDates = createSelector(
  selectCurrentProjectName,
  selectCurrentProjectLiveStart,
  selectCurrentProjectLiveStop,
  (name, liveStart, liveStop) => Boolean(name && liveStart && liveStop),
);
